import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, Col, Container, Row } from 'react-bootstrap';
import { LuArrowLeft, LuArrowRight } from 'react-icons/lu';

import GradientCTA from '../app/components/GradientCTA';
import ResourcesHeroWaves from '../app/components/ResourcesHeroWaves';
import SbrIcon from '../app/components/SbrIcon';
import {
  blogPostPath,
  fetchLatestSbrBlogPosts,
  fetchSbrBlogPost,
  formatBlogDate,
  resolveBlogImageUrl,
  SBR_BLOG_INDEX_PATH,
  type SbrBlogPost,
  type SbrBlogPostSummary,
} from '../lib/sbrBlog';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<SbrBlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [prevPost, setPrevPost] = useState<SbrBlogPostSummary | null>(null);
  const [nextPost, setNextPost] = useState<SbrBlogPostSummary | null>(null);

  useEffect(() => {
    if (!slug) return;
    const postSlug = slug;

    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const [article, all] = await Promise.all([
          fetchSbrBlogPost(postSlug),
          fetchLatestSbrBlogPosts(),
        ]);
        if (cancelled) return;

        if (!article) {
          navigate('/resources#blog', { replace: true });
          return;
        }

        setPost(article);
        const idx = all.findIndex((p) => p.slug === postSlug);
        setPrevPost(idx > 0 ? all[idx - 1] : null);
        setNextPost(idx >= 0 && idx < all.length - 1 ? all[idx + 1] : null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  if (loading || !post) {
    return (
      <section className="resources-content bg-sbr-light py-5 py-lg-6">
        <Container>
          <p className="text-secondary mb-0">Loading article…</p>
        </Container>
      </section>
    );
  }

  return (
    <>
      <section className="hero-pattern text-white resources-hero blog-post-hero">
        <Container className="resources-hero__container">
          <Breadcrumb className="solution-hero-breadcrumb mb-3">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/resources#blog' }}>
              Resources
            </Breadcrumb.Item>
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: SBR_BLOG_INDEX_PATH }}>
              Blog
            </Breadcrumb.Item>
            <Breadcrumb.Item active>{post.title}</Breadcrumb.Item>
          </Breadcrumb>

          <Row className="resources-hero__row gy-3 align-items-center">
            <Col lg={7}>
              <p className="blog-post-hero__eyebrow mb-2">
                <span className={`resources-blog-card__tag resources-blog-card__tag--${post.categoryTone}`}>
                  {post.categoryLabel}
                </span>
              </p>
              <h1 className="display-5 fw-bold mb-3">{post.title}</h1>
              <p className="lead text-white-50 mb-0">
                {formatBlogDate(post.publishedAt)} · {post.readMinutes} min read
              </p>
            </Col>
            <Col lg={5} className="resources-hero__media-col">
              <ResourcesHeroWaves />
            </Col>
          </Row>
        </Container>
      </section>

      <section className="resources-content bg-sbr-light py-5 py-lg-6">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={9}>
              {post.imageUrl ? (
                <div className="blog-post__featured-media mb-4">
                  <img src={resolveBlogImageUrl(post.imageUrl)} alt="" />
                </div>
              ) : null}

              <article
                className="blog-post__content"
                dangerouslySetInnerHTML={{ __html: post.contentHtml }}
              />

              <nav className="blog-post__pager" aria-label="More blog posts">
                {prevPost ? (
                  <Link to={blogPostPath(prevPost.slug)} className="blog-post__pager-link blog-post__pager-link--prev">
                    <SbrIcon icon={LuArrowLeft} size={18} />
                    <span>
                      <span className="blog-post__pager-label">Previous</span>
                      <span className="blog-post__pager-title">{prevPost.title}</span>
                    </span>
                  </Link>
                ) : (
                  <span />
                )}
                {nextPost ? (
                  <Link to={blogPostPath(nextPost.slug)} className="blog-post__pager-link blog-post__pager-link--next">
                    <span>
                      <span className="blog-post__pager-label">Next</span>
                      <span className="blog-post__pager-title">{nextPost.title}</span>
                    </span>
                    <SbrIcon icon={LuArrowRight} size={18} />
                  </Link>
                ) : (
                  <span />
                )}
              </nav>

              <p className="blog-post__back-wrap">
                <Link to={SBR_BLOG_INDEX_PATH} className="resources-podcast__view-all">
                  <span>Back to all blog posts</span>
                  <SbrIcon icon={LuArrowRight} size={18} className="resources-podcast__view-all-icon" />
                </Link>
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <GradientCTA
        icon={<i className="bi bi-calendar-event fs-4" aria-hidden />}
        title={
          <>
            <div>Want help putting these ideas into practice?</div>
            <div className="sbr-cta__subtitle fw-normal opacity-75 mt-2">
              Schedule a working session with our team.
            </div>
          </>
        }
      />
    </>
  );
}
